import React from "react";
import { FiDownload } from "react-icons/fi";
import { getFieldAnalysis, renderFieldAnalysis } from './utils/fieldsUtils';
import { debug } from "utils";
import { BsQuestionCircle, BsCheckCircle } from "react-icons/bs";
import { HiOutlineExclamationCircle } from "react-icons/hi";

import "./assets/form-builder-summary.css";
/**
 * FormBuilderSummary
 * -------------------
 * Génère une synthèse lisible basée sur :
 *  - schema JSON (source of truth)
 *  - data (frData)
 *  - errors
 *  - visibility
 */
export default function FormBuilderSummary({
  schema,
  data,
  errors = {},
  visibility = {},
  onFileClick = null,
  analysis = {},
  readOnly = false
}) {
  if (!schema || !schema.properties) return null;


  const tabKeys = Object.keys(schema.properties);
  const hasMultipleTabs = tabKeys.length > 1;
  return (
    <div className="fb-summary">
      {Object.entries(schema.properties).map(([tabKey, tabSchema]) => {
        const tabData = data?.[tabKey] || {};
        const tabErrors = errors?.[tabKey] || {};
        const tabVisible = visibility?.[tabKey] ?? true;
        if (!tabVisible) return null;
        return (
          <section key={tabKey} className="fb-summary-section">
            {hasMultipleTabs && (
              <h2 className="">
                {tabSchema.title || tabKey}
              </h2>
            )}
            <div className="fb-summary-data">
              {renderObject(
                tabSchema,
                tabData,
                tabErrors,
                visibility?.[tabKey],
                onFileClick,
                analysis,
                tabKey,
                readOnly
              )}
            </div>
          </section>
        );
      })}

    </div>
  );
}
/* -----------------------------------------------------------
 * Rendu d’un objet (TAB ou sous-objet)
 * ----------------------------------------------------------- */
function renderObject(
  schema,
  data = {},
  errors = {},
  visibility = {},
  onFileClick = null,
  analysis = null,
  tabKey = null,
  readOnly = false
) {
  if (!schema.properties) return null;
  function protectColons(text) {
    if (typeof text !== "string") return text;
    return text.replace(/\s:/g, "\u00A0:");
  }
  const displayMap = schema["x-display"] || null;

  return (
    <ul>

      {Object.entries(schema.properties).map(([key, field]) => {
        // 1) VISIBILITY MAP
        if (visibility && visibility[key] === false) return null;
        // 2) X-DISPLAY
        if (displayMap && displayMap[key] === false) return null;
        // 3) X-HIDDEN (conditions dynamiques x-show-if)
        if (field["x-hidden"] === true) return null;
        // 4) masqué dans le résumé
        if (
          field.hiddenInSummary === true
        ) {
          return null;
        }
        field.parents = [tabKey]
        const fieldAnalysis = getFieldAnalysis(analysis, tabKey, key)

        const val = data?.[key];
        const err = errors?.[key];
        const hasError =
          err !== undefined &&
          err !== null &&
          err !== "" &&
          err !== false;
        // --------------------------------------------
        // STATIC TEXT
        // --------------------------------------------
        if (field["x-ui"] === "staticText") {
          return (
            <li key={key} className="fb-static">
              <blockquote className="text-muted">
                {protectColons(field.content || "(texte vide)")}
              </blockquote>
            </li>
          );
        }
        // --------------------------------------------
        // STATIC TITLE
        // --------------------------------------------
        if (field["x-ui"] === "staticTitle") {
          const headingTag =
            field.properties?.heading?.default || "h4";
          const TitleTag = headingTag;
          return (
            <li key={key} className="fb-static">
              <TitleTag className="">
                {protectColons(
                  field.properties?.title?.default || "(Titre vide)"
                )}
              </TitleTag>
              {field.properties?.subtitle?.default && (
                <div className="text-muted">
                  {protectColons(field.properties.subtitle.default)}
                </div>
              )}
            </li>
          );
        }
        // --------------------------------------------
        // CHAMP OBJET
        // --------------------------------------------
        if (field.type === "object") {
          return (
            <li
              key={key}
              className={hasError ? "text-danger" : ""}
            >
              {field.title || key}&nbsp;:
              <div className="ms-3">
                {renderObject(
                  field,
                  val || {},
                  err || {},
                  visibility?.[key] || {},
                  onFileClick,
                  readOnly
                )}
              </div>
            </li>
          );
        }
        // --------------------------------------------
        // FILE FIELD
        // --------------------------------------------
        if (field["x-ui"] === "file") {
          return (
            <li
              key={key}
              className={`analysis-status analysis-status-${fieldAnalysis?.status || "none"} mb-2`}
            >
              {field.title || key}&nbsp;:
              <div>

                {renderFileValue(val, field, fieldAnalysis, readOnly)}


              </div>
            </li>
          );
        }
        // --------------------------------------------
        // CHAMP SIMPLE
        // --------------------------------------------
        return (
          <li key={key} className={hasError ? "text-danger" : ""}>
            {field.title || key}&nbsp;:{" "}
            <strong>
              {val !== undefined && val !== null && val !== ""
                ? String(val)
                : "(non renseigné)"}
            </strong>
          </li>
        );
      })}
    </ul>
  );
  /* --- gestion fichiers --- */
  function renderFileValue(fileArray, field, fieldAnalysis = null) {
    if (!Array.isArray(fileArray) || fileArray.length === 0) {
      return (
        <span className="text-muted">
          (non renseigné)
        </span>
      );
    }
    return (
      <ul className="list-unstyled text-dark">
        {fileArray.map((file, i) => {
          const fileAnalysis =
            fieldAnalysis?.files?.[file.path] || null;
          return (
            <li key={i} className="mb-1 ">
              <div className="d-flex align-items-center gap-1">
                {fileAnalysis?.status === "accepted" && (
                  <span className="text-success small d-flex align-items-center">
                    <BsCheckCircle size={20} />
                  </span>
                )}
                {fileAnalysis?.status === "rejected" && (
                  <span className="text-danger d-flex align-items-start gap-1">
                    <HiOutlineExclamationCircle size={20} />
                  </span>
                )}
                {!fileAnalysis?.status && (
                  <span className="text-warning d-flex align-items-start gap-1">
                    <BsQuestionCircle size={20} />
                  </span>
                )}
                <FiDownload
                  className=" d-inline-block"
                  style={{ top: "-1px" }}
                />
                {onFileClick ? (
                  <span
                    className="decoration-on-hover pointer"
                    onClick={() => onFileClick(file, field)}
                  >
                    {file.name}
                  </span>
                ) : (
                  <strong>{file.name}</strong>
                )}
                {/* --- ANALYSIS --- */}
              </div>
              {fileAnalysis?.status === "rejected" && fileAnalysis.comment && (
                <span className="fst-italic text-danger small">
                  {fileAnalysis.comment}
                </span>
              )}
            </li>
          );
        })}

      </ul>
    );
  }

}
